import type { Cell } from '../model/types';

const samePointTolerance = 0.0001;

function distanceBetween(start: Cell, end: Cell): number {
  return Math.hypot(end.x - start.x, end.y - start.y);
}

function samePoint(left: Cell, right: Cell): boolean {
  return Math.abs(left.x - right.x) < samePointTolerance &&
    Math.abs(left.y - right.y) < samePointTolerance;
}

export function cumulativeLengths(points: readonly Cell[]): readonly number[] {
  const cumulative = new Array<number>(points.length);
  if (points.length === 0) {
    return cumulative;
  }

  cumulative[0] = 0;
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const point = points[index];
    if (previous === undefined || point === undefined) {
      continue;
    }
    cumulative[index] = (cumulative[index - 1] ?? 0) + distanceBetween(previous, point);
  }
  return cumulative;
}

function segmentIndexAtDistance(
  points: readonly Cell[],
  cumulative: readonly number[],
  distance: number,
): number {
  for (let index = 1; index < points.length; index += 1) {
    const segmentStartDistance = cumulative[index - 1];
    const segmentEndDistance = cumulative[index];
    if (
      segmentStartDistance === undefined ||
      segmentEndDistance === undefined ||
      segmentStartDistance === segmentEndDistance
    ) {
      continue;
    }
    if (segmentEndDistance >= distance) {
      return index;
    }
  }
  return points.length;
}

function coordinateAtDistance(
  points: readonly Cell[],
  cumulative: readonly number[],
  distance: number,
  segmentIndex: number,
  axis: 'x' | 'y',
): number {
  const segmentStart = points[segmentIndex - 1];
  const segmentEnd = points[segmentIndex];
  const segmentStartDistance = cumulative[segmentIndex - 1];
  const segmentEndDistance = cumulative[segmentIndex];
  if (
    segmentStart === undefined ||
    segmentEnd === undefined ||
    segmentStartDistance === undefined ||
    segmentEndDistance === undefined
  ) {
    return points[points.length - 1]?.[axis] ?? 0;
  }
  const ratio = (distance - segmentStartDistance) /
    (segmentEndDistance - segmentStartDistance);
  return segmentStart[axis] + (segmentEnd[axis] - segmentStart[axis]) * ratio;
}

export function writePolylineWindow(
  points: readonly Cell[],
  cumulative: readonly number[],
  startDistance: number,
  endDistance: number,
  out: Array<{ x: number; y: number }>,
): number {
  if (points.length !== cumulative.length) {
    throw new Error('Points and cumulative lengths must have matching lengths.');
  }
  if (points.length === 0) {
    return 0;
  }
  if (out.length === 0) {
    throw new Error('Polyline window output is too small.');
  }

  const totalLength = cumulative[cumulative.length - 1] ?? 0;
  const start = Math.min(Math.max(startDistance, 0), totalLength);
  const end = Math.min(Math.max(endDistance, start), totalLength);
  const startSegmentIndex = segmentIndexAtDistance(points, cumulative, start);
  const endSegmentIndex = segmentIndexAtDistance(points, cumulative, end);
  const endX = coordinateAtDistance(points, cumulative, end, endSegmentIndex, 'x');
  const endY = coordinateAtDistance(points, cumulative, end, endSegmentIndex, 'y');
  const firstTarget = out[0];
  if (firstTarget === undefined) {
    throw new Error('Polyline window output is too small.');
  }
  let count = 1;
  firstTarget.x = coordinateAtDistance(points, cumulative, start, startSegmentIndex, 'x');
  firstTarget.y = coordinateAtDistance(points, cumulative, start, startSegmentIndex, 'y');

  for (let index = 1; index < points.length; index += 1) {
    const distance = cumulative[index];
    const point = points[index];
    if (distance === undefined || point === undefined || distance <= start || distance >= end) {
      continue;
    }
    const previous = out[count - 1];
    if (previous !== undefined && samePoint(previous, point)) {
      continue;
    }
    const target = out[count];
    if (target === undefined) {
      throw new Error('Polyline window output is too small.');
    }
    target.x = point.x;
    target.y = point.y;
    count += 1;
  }

  const previous = out[count - 1];
  if (previous !== undefined && Math.abs(previous.x - endX) < samePointTolerance &&
    Math.abs(previous.y - endY) < samePointTolerance) {
    return count;
  }
  const endTarget = out[count];
  if (endTarget === undefined) {
    throw new Error('Polyline window output is too small.');
  }
  endTarget.x = endX;
  endTarget.y = endY;
  return count + 1;
}
